import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Hammer,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { dueBaseLabel } from './paymentTermUtils';
import {
  PurchaseOrder,
  PURCHASE_ORDER_STATUS_OPTIONS,
  FREIGHT_RESPONSIBILITY_OPTIONS,
} from '../../types/purchaseOrder';
import { formatCnpj } from '../../types/company';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR');
};

const getStatusBadge = (status: number) => {
  const opt = PURCHASE_ORDER_STATUS_OPTIONS.find(o => o.value === status);
  if (!opt) return <span className="text-slate-500">—</span>;
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[opt.color]}`}>
      {opt.label}
    </span>
  );
};

// ─── Field display ───────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
  </div>
);

// ─── Component ───────────────────────────────────────────────────

export const PurchaseOrderDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await purchaseOrderService.getPurchaseOrderById(parseInt(id));
        setOrder(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar pedido');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await purchaseOrderService.deletePurchaseOrder(parseInt(id));
      navigate('/purchase-orders');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir pedido');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      setPdfLoading(true);
      setPdfError(null);
      await purchaseOrderService.downloadPdf(parseInt(id));
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Falha ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/purchase-orders')}><ArrowLeft size={20} /></Button>
          <div className="h-8 bg-slate-200 rounded w-56 animate-pulse" />
        </div>
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent><div className="h-24 bg-slate-200 rounded animate-pulse" /></CardContent></Card>
        ))}
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/purchase-orders')}><ArrowLeft size={20} /></Button>
          <h1 className="text-2xl font-bold text-slate-900">Pedido não encontrado</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error || 'Pedido não encontrado.'}</div>
      </div>
    );
  }

  const items = order.items || [];
  const installments = order.installments || [];
  const freightLabel = FREIGHT_RESPONSIBILITY_OPTIONS.find(o => o.value === order.freight_responsibility)?.label || '—';

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/purchase-orders')}><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Pedido #{order.order_number || order.purchase_order_id}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Criado em {formatDateTime(order.created_at)} por {order.created_by}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <PermissionGuard permission="purchase_orders.read">
            <Button variant="outline" icon={<FileText size={18} />} loading={pdfLoading} onClick={handleDownloadPdf}>
              Gerar PDF
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="purchase_orders.write">
            <Button variant="outline" icon={<Edit size={18} />} onClick={() => navigate(`/purchase-orders/${id}/edit`)}>
              Editar
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="purchase_orders.delete">
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" icon={<Trash2 size={18} />} onClick={() => setDeleteOpen(true)}>
              Excluir
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {pdfError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {pdfError}
        </div>
      )}

      {/* ── Dados Gerais ──────────────────────────────────────── */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Empresa">
              <span className="font-medium">{order.company_name || '—'}</span>
              {order.company_document && <span className="block text-xs text-slate-400">CNPJ: {formatCnpj(order.company_document)}</span>}
            </Field>
            <Field label="Situação">
              <div className="flex flex-col items-start gap-1">
                {order.is_priority === true && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    <AlertTriangle size={11} /> PRIORITÁRIO
                  </span>
                )}
                {getStatusBadge(order.status)}
              </div>
            </Field>
            <Field label="Data do Pedido">{formatDate(order.order_date)}</Field>
            <Field label="Data Prevista">{formatDate(order.expected_date)}</Field>
            <Field label="Referência">{order.purchase_order_reference || '—'}</Field>
            <Field label="Fornecedor">
              <span className="font-medium">{order.provider_name || '—'}</span>
              {order.provider_document && <span className="block text-xs text-slate-400">{order.provider_document}</span>}
            </Field>
            <Field label="Modelo Pagamento">{order.payment_method_model_name || '—'}</Field>
            <Field label="Status">{order.is_active ? 'Ativo' : 'Inativo'}</Field>
            <Field label="Última Atualização">{formatDateTime(order.updated_at)}</Field>
          </dl>
        </CardContent>
      </Card>

      {/* ── Pedidos de Venda vinculados ───────────────────────── */}
      {(order.sales_orders?.length ?? 0) > 0 && (
        <Card>
          <CardHeader title={`Pedidos de Venda vinculados (${order.sales_orders!.length})`} />
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Pedido</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data Necessária</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Envio Agendado</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Prioridade</th>
                  </tr>
                </thead>
                <tbody>
                  {order.sales_orders!.map(link => (
                    <tr key={link.order_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="py-2 px-4">
                        <RouterLink to={`/sales-orders/${link.order_id}`} className="text-emerald-600 font-medium hover:underline">
                          PV #{link.order_id}
                        </RouterLink>
                      </td>
                      <td className="py-2 px-4 text-slate-700">{link.customer_name || '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{link.order_status || '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{link.request_delivery_time ? formatDateTime(link.request_delivery_time) : '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{link.scheduled_ship_date ? formatDate(link.scheduled_ship_date) : '—'}</td>
                      <td className="py-2 px-4">
                        {link.is_priority ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <AlertTriangle size={10} /> PRIORITÁRIO
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">Normal</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Itens ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={`Itens do Pedido (${items.length})`} />
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhum item neste pedido.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-4">Tipo</th>
                    <th className="py-2 px-4">SKU</th>
                    <th className="py-2 px-4">Descrição</th>
                    <th className="py-2 px-4">Cód. Fornecedor</th>
                    <th className="py-2 px-4">Unid.</th>
                    <th className="py-2 px-4 text-right">Valor Unit.</th>
                    <th className="py-2 px-4 text-right">Qtde</th>
                    <th className="py-2 px-4 text-right">IPI %</th>
                    <th className="py-2 px-4 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const isRaw = !!item.raw_material_id;
                    return (
                      <tr key={item.purchase_order_item_id || idx} className="border-b border-slate-100">
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isRaw ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isRaw ? <Hammer size={12} /> : <Package size={12} />}
                            {isRaw ? 'MP' : 'Prod'}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-slate-600 font-mono text-xs">{item.sku || '—'}</td>
                        <td className="py-2 px-4 text-slate-900">{item.description}</td>
                        <td className="py-2 px-4 text-slate-600">{item.supplier_code || '—'}</td>
                        <td className="py-2 px-4">{item.unit || '—'}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(item.unit_value)}</td>
                        <td className="py-2 px-4 text-right">{item.quantity}</td>
                        <td className="py-2 px-4 text-right">{item.ipi_rate != null ? `${item.ipi_rate}%` : '—'}</td>
                        <td className="py-2 px-4 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td colSpan={7} className="py-2 px-4 text-right font-semibold text-slate-700">Total Produtos:</td>
                    <td className="py-2 px-4 text-right font-bold text-emerald-700">{formatCurrency(order.total_products)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Transporte ────────────────────────────────────────── */}
      <Card>
        <CardHeader title="Transporte" />
        <CardContent>
          <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Field label="Transportadora">{order.carrier || '—'}</Field>
            <Field label="Frete por conta">{freightLabel}</Field>
            <Field label="Valor do Frete">{formatCurrency(order.freight_cost)}</Field>
            <Field label="Peso Bruto">{order.gross_weight ? `${order.gross_weight} kg` : '—'}</Field>
            <Field label="Volumes">{order.volumes ?? '—'}</Field>
          </dl>
        </CardContent>
      </Card>

      {/* ── Desconto & Tributos ───────────────────────────────── */}
      <Card>
        <CardHeader title="Desconto e Tributação" />
        <CardContent>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Desconto">
              {order.discount_value != null
                ? `${order.discount_unit === 'PERCENTUAL' ? order.discount_value + '%' : formatCurrency(order.discount_value)}`
                : '—'}
            </Field>
            <Field label="Total ICMS">{formatCurrency(order.total_icms)}</Field>
            <Field label="Total IPI">{formatCurrency(order.total_ipi)}</Field>
            <Field label="Total Geral">
              <span className="text-lg font-bold text-emerald-700">{formatCurrency(order.total)}</span>
            </Field>
          </dl>
        </CardContent>
      </Card>

      {/* ── Parcelas ──────────────────────────────────────────── */}
      <Card>
        <CardHeader title={`Parcelas (${installments.length})`} />
        <CardContent className="p-0">
          {installments.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhuma parcela registrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-4 w-10">#</th>
                    <th className="py-2 px-4">Vencimento</th>
                    <th className="py-2 px-4 text-right">Valor</th>
                    <th className="py-2 px-4">Forma de Pagamento</th>
                    <th className="py-2 px-4">Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((inst, idx) => (
                    <tr key={inst.purchase_order_installment_id || idx} className="border-b border-slate-100">
                      <td className="py-2 px-4 text-slate-500">{idx + 1}</td>
                      <td className="py-2 px-4">
                        {formatDate(inst.due_date)}
                        {inst.due_date_base != null && (
                          <span className="block text-[10px] text-slate-400">{dueBaseLabel(inst.due_date_base, inst.due_days)}</span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(inst.value)}</td>
                      <td className="py-2 px-4">{inst.payment_method_name || '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{inst.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td />
                    <td className="py-2 px-4 text-right font-semibold text-slate-700">Total:</td>
                    <td className="py-2 px-4 text-right font-bold text-emerald-700">
                      {formatCurrency(installments.reduce((s, i) => s + i.value, 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Observações ───────────────────────────────────────── */}
      {(order.notes || order.internal_notes) && (
        <Card>
          <CardHeader title="Observações" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {order.notes && (
                <div>
                  <h4 className="text-xs text-slate-500 uppercase mb-1">Observações</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}
              {order.internal_notes && (
                <div>
                  <h4 className="text-xs text-slate-500 uppercase mb-1">Observações Internas</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.internal_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Excluir Pedido de Compra"
        message="Tem certeza que deseja excluir este pedido de compra? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
